/**
 * ACCOUNTS.md §4 忘记密码自助 — `/forgot`.
 *
 * The server answers the **same generic body** whatever the account state is,
 * so this page must not try to be helpful: no 「这个账号不存在」, no 「你没绑邮箱」.
 * It shows exactly the sentence the server sent, and that is the whole point —
 * a stranger typing usernames learns nothing.
 *
 * When the deployment has no SMTP at all (`features.forgot_password` is false
 * on `GET health`), the page says so up front and points at the teacher, which
 * is the path that has always existed.
 */
import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Field, Icon, Input } from "@xinghai/ui";
import { authApi, healthApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { COPYRIGHT_LINE } from "../../lib/constants";
import { useDesignSystemTheme } from "@xinghai/ui";
import { useTheme } from "../../app/theme";

/** 没配 SMTP 时的兜底文案，与登录页保持一致。 */
export const NO_SMTP_TEXT = "这套部署没有开通邮件找回。请找课程老师或管理员要一个一次性重置链接。";

export function ForgotPasswordPage() {
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const [account, setAccount] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const health = useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => healthApi.get(signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // treat "unknown" as available: the form still answers generically
  const smtp = health.data?.features?.forgot_password === true;
  const sms = health.data?.features?.sms_recovery === true;
  const selfServe = health.data == null || smtp || sms;
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [resetOk, setResetOk] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (account.trim().length === 0) return;
    setBusy(true);
    try {
      const r = await authApi.forgot(account.trim());
      // whatever happened server-side, this is the only thing we may render
      setSent(r.message);
    } catch (err) {
      setError(friendlyMessage(err, "请求没能发出去，请稍后再试。"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="xz-login">
      <form className="xz-login__card gz-panel gz-panel--raised" onSubmit={(e) => void onSubmit(e)}>
        <img
          className="xz-login__mark"
          src="/design-system/assets/brand-v2/mark-color.svg"
          alt=""
          aria-hidden="true"
          width={48}
          height={48}
        />
        <h1 className="gz-type-heading">找回密码</h1>

        {!selfServe ? (
          <>
            <p className="gz-notice" role="status">
              <Icon id="ui-info" size={20} />
              {NO_SMTP_TEXT}
            </p>
            <Link className="gz-button gz-button--primary" to="/login">
              回到登录
            </Link>
          </>
        ) : sent ? (
          <>
            {/* The one sentence the server sent — nothing added, nothing inferred. */}
            <p className="gz-notice gz-notice--success" role="status" data-testid="forgot-result">
              <Icon id="ui-check-circle" size={20} />
              {sent}
            </p>
            <p className="gz-caption">
              没收到？检查一下垃圾邮件箱。如果绑定了手机，也可以填写短信验证码。老师发的重置链接仍然可用。
            </p>
            {sms && !resetOk && (
              <>
                <Field label="短信验证码">
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
                <Field label="新密码" hint="至少 10 位">
                  <Input
                    type="password"
                    value={password}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  block
                  onClick={() => {
                    setError(null);
                    setBusy(true);
                    void authApi
                      .recoverSms(account.trim(), code.trim(), password)
                      .then(() => setResetOk(true))
                      .catch((err) => setError(friendlyMessage(err, "验证码无效或已过期")))
                      .finally(() => setBusy(false));
                  }}
                >
                  用短信验证码重设密码
                </Button>
              </>
            )}
            {resetOk && <p className="gz-notice gz-notice--success">密码已更新，请用新密码登录。</p>}
            <Link className="gz-button gz-button--primary" to="/login">
              回到登录
            </Link>
          </>
        ) : (
          <>
            <p className="gz-muted">
              输入你的用户名，或者你在「设置」里绑定并验证过的邮箱。我们会把一次性重置链接发到那个邮箱。
            </p>
            <Field label="用户名或邮箱">
              <Input
                name="account"
                value={account}
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                onChange={(e) => setAccount(e.target.value)}
              />
            </Field>
            {error && (
              <p className="gz-notice gz-notice--error" role="alert">
                <Icon id="ui-error" size={20} />
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              发送重置链接
            </Button>
            <p className="gz-caption">没绑过邮箱？找课程老师或管理员要一个一次性重置链接。</p>
            <p className="gz-caption">
              <Link to="/login">回到登录</Link>
            </p>
          </>
        )}
        <p className="gz-caption xz-login__org">{COPYRIGHT_LINE}</p>
      </form>
    </main>
  );
}
